import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toBlob } from "html-to-image";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { BadgeCheck, ExternalLink, MessageCircle, Mail, Clock, Headset, Printer, Download, Share2, Loader2, ArrowLeft } from "lucide-react";
import { useFetch } from "@/lib/hooks";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { UAvatar, Empty } from "@/components/common";
import { SubPage } from "@/pages/ProfilePages";

const fmtJoin = (iso) => (iso ? new Date(iso).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" }) : "-");

export function IdCardPage() {
  const { data } = useFetch("/worker/id-card");
  const { settings } = useAuth();
  const cardRef = useRef(null);
  const [busy, setBusy] = useState("");
  const render = async () => {
    const n = cardRef.current;
    const opts = { pixelRatio: 3, cacheBust: true, width: n.offsetWidth, height: n.offsetHeight,
      style: { margin: "0", boxShadow: "none" } };
    try {
      return await toBlob(cardRef.current, opts);
    } catch (e) {
      return toBlob(cardRef.current, { ...opts, skipFonts: true });
    }
  };
  const fileName = () => `ID-Card-${data.id_number}.png`;
  const download = async () => {
    setBusy("download");
    try {
      const blob = await render();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = fileName();
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 2000);
      toast.success("ID Card tersimpan sebagai gambar");
    } catch (e) {
      toast.error("Gagal menyimpan gambar");
    } finally {
      setBusy("");
    }
  };
  const share = async () => {
    setBusy("share");
    try {
      const file = new File([await render()], fileName(), { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "ID Card Worker", text: `${data.name} - ${data.id_number}` });
      } else {
        toast.info("Perangkat tidak mendukung bagikan langsung, gunakan Unduh Gambar");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Gagal membagikan");
    } finally {
      setBusy("");
    }
  };
  if (!data) return <SubPage title="ID Card Worker" testid="id-card-page"><div className="h-96 rounded-3xl bg-slate-200 animate-pulse" /></SubPage>;
  return (
    <SubPage title="ID Card Worker" testid="id-card-page">
      <div ref={cardRef} data-testid="worker-id-card" className="relative mx-auto max-w-[340px] aspect-[54/86] rounded-[1.75rem] overflow-hidden shadow-2xl bg-white print:shadow-none">
        <div className="relative h-[38%] grain bg-gradient-to-br from-emerald-600 via-teal-700 to-slate-900 px-5 pt-5">
          <div className="absolute -right-12 -top-12 w-40 h-40 rounded-full bg-white/10" />
          <div className="relative flex items-center justify-between">
            <div className="bg-white rounded-xl px-2 py-1"><Logo /></div>
            <span className="text-[10px] font-extrabold tracking-[0.2em] text-white/80">WORKER ID</span>
          </div>
        </div>
        <div className="absolute left-1/2 top-[24%] -translate-x-1/2 p-1.5 bg-white rounded-full shadow-lg">
          <UAvatar path={data.photo} name={data.name} className="w-28 h-28" />
        </div>
        <div className="pt-[22%] px-5 text-center">
          <p data-testid="id-card-name" className="font-display text-xl font-extrabold text-slate-900 leading-tight">{data.name}</p>
          {data.verified && (
            <span data-testid="id-card-verified" className="inline-flex items-center gap-1 mt-1.5 px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-[11px] font-extrabold">
              <BadgeCheck className="w-4 h-4 text-white fill-blue-500" /> TERVERIFIKASI
            </span>
          )}
          <p className="text-[11px] text-slate-500 mt-1.5 line-clamp-1">{data.skills.join(" · ")}</p>
          <div className="mt-3 flex items-center gap-3 text-left">
            <div className="p-1.5 bg-white border rounded-xl shrink-0">
              <QRCodeSVG data-testid="id-card-qr" value={JSON.stringify({ nama: data.name, id: data.id_number })} size={82} />
            </div>
            <div className="space-y-1.5 min-w-0">
              <div><p className="text-[9px] uppercase tracking-wider text-slate-400 font-bold">Nomor ID</p><p data-testid="id-card-number" className="font-mono font-bold text-sm text-slate-900">{data.id_number}</p></div>
              <div><p className="text-[9px] uppercase tracking-wider text-slate-400 font-bold">Bergabung</p><p data-testid="id-card-joined" className="text-xs font-bold text-slate-800">{fmtJoin(data.joined_at)}</p></div>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 inset-x-0 h-7 bg-slate-900 text-white/70 text-[9px] font-bold tracking-widest grid place-items-center">{(settings.app_name || "UKerja").toUpperCase()} · WORKER RESMI</div>
      </div>
      <div className="mt-5 grid grid-cols-2 gap-2 max-w-[340px] mx-auto print:hidden">
        <button data-testid="download-id-card-btn" onClick={download} disabled={!!busy} className="h-12 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-60">
          {busy === "download" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}Unduh Gambar
        </button>
        <button data-testid="share-id-card-btn" onClick={share} disabled={!!busy} className="h-12 rounded-full border-2 border-emerald-600 text-emerald-700 font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-60">
          {busy === "share" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}Bagikan
        </button>
      </div>
      <button data-testid="print-id-card-btn" onClick={() => window.print()} className="mt-3 mx-auto flex items-center gap-2 h-10 px-5 rounded-full text-slate-600 font-bold text-sm hover:bg-slate-100 print:hidden"><Printer className="w-4 h-4" />Cetak / Simpan PDF</button>
    </SubPage>
  );
}

export function MarketplacePage() {
  const { settings } = useAuth();
  const url = settings.marketplace_url;
  const nav = useNavigate();
  if (url) {
    return (
      <div data-testid="marketplace-page" className="fixed inset-x-0 top-14 bottom-[calc(max(env(safe-area-inset-bottom),14px)+4.5rem)] max-w-md mx-auto z-30 flex flex-col bg-white">
        <div className="h-10 shrink-0 flex items-center gap-2 px-3 border-b bg-white">
          <button data-testid="back-btn" onClick={() => nav(-1)} className="w-8 h-8 rounded-full hover:bg-slate-100 grid place-items-center"><ArrowLeft className="w-4 h-4" /></button>
          <p className="font-display font-bold text-sm flex-1">Marketplace</p>
          <a data-testid="marketplace-open-tab" href={url} target="_blank" rel="noreferrer" title="Buka di tab baru" className="w-8 h-8 rounded-full hover:bg-slate-100 grid place-items-center text-emerald-700"><ExternalLink className="w-4 h-4" /></a>
        </div>
        <iframe data-testid="marketplace-iframe" title="Marketplace" src={url} className="w-full flex-1 border-0" allow="geolocation; payment; clipboard-write" />
      </div>
    );
  }
  return (
    <SubPage title="Marketplace" testid="marketplace-page">
      {!url ? <Empty title="Marketplace belum diatur" desc="Admin belum mengisi alamat URL marketplace." /> : (
        <div>
        </div>
      )}
    </SubPage>
  );
}

export function SupportPage() {
  const { settings } = useAuth();
  const wa = (settings.cs_whatsapp || "").replace(/\D/g, "").replace(/^0/, "62");
  const items = [
    wa && [MessageCircle, "Chat WhatsApp", settings.cs_whatsapp, `https://wa.me/${wa}`, "cs-whatsapp-btn", "bg-emerald-50 text-emerald-700"],
    settings.cs_email && [Mail, "Kirim Email", settings.cs_email, `mailto:${settings.cs_email}`, "cs-email-btn", "bg-blue-50 text-blue-700"],
  ].filter(Boolean);
  return (
    <SubPage title="Layanan Pelanggan" testid="support-page">
      <div className="rounded-3xl p-5 bg-gradient-to-br from-slate-900 to-emerald-900 text-white mb-4">
        <Headset className="w-8 h-8 text-emerald-300" />
        <p className="font-display text-xl font-extrabold mt-2">Ada yang bisa kami bantu?</p>
        <p className="text-sm text-white/70">Tim {settings.app_name || "UKerja"} siap membantu kendala pesanan, koin, maupun akun Anda.</p>
        {settings.cs_hours && <p className="text-xs mt-3 flex items-center gap-1 text-white/80"><Clock className="w-3.5 h-3.5" />{settings.cs_hours}</p>}
      </div>
      {items.length === 0 ? <Empty title="Kontak belum tersedia" desc="Admin belum mengatur kontak layanan pelanggan." /> : (
        <div className="space-y-3">
          {items.map(([I, l, v, href, tid, cls]) => (
            <a key={tid} data-testid={tid} href={href} target="_blank" rel="noreferrer" className="flex items-center gap-3 bg-white rounded-2xl border p-4 hover:shadow-md transition-shadow">
              <span className={`w-11 h-11 rounded-xl grid place-items-center ${cls}`}><I className="w-5 h-5" /></span>
              <span><span className="block font-bold text-sm">{l}</span><span className="block text-xs text-slate-500">{v}</span></span>
            </a>
          ))}
        </div>
      )}
    </SubPage>
  );
}
